import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import type { Coupon, CouponUsage, LotteryEvent, LotteryStockDoc } from '@kawai/shared';
import {
  fetchCouponsByIds,
  fetchLineDisplayNames,
  fetchLotteryCouponUsages,
  fetchLotteryEvents,
  fetchLotteryStock,
  fetchLotteryTopUsers,
} from '../repositories/lottery.js';
import { pickLatestLotteryEvent } from '../lotteryStatusStats.js';

/** M-164: 抽選状況ページ用 hooks（読取のみ / 集計は純関数 lotteryStatusStats に任せる）。 */

/** cache key: `['promo','lottery','event', tenantId]`。最新 1 件（無ければ null）。 */
export function useLotteryEvent(
  tenantId: string | null | undefined,
): UseQueryResult<LotteryEvent | null, Error> {
  return useQuery({
    queryKey: ['promo', 'lottery', 'event', tenantId],
    queryFn: () => fetchLotteryEvents(tenantId as string),
    select: pickLatestLotteryEvent,
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });
}

/** cache key: `['promo','lottery','stock', tenantId, eventId]`。イベント中は 1 分で再取得。 */
export function useLotteryStock(
  tenantId: string | null | undefined,
  eventId: string | null | undefined,
): UseQueryResult<LotteryStockDoc[], Error> {
  return useQuery({
    queryKey: ['promo', 'lottery', 'stock', tenantId, eventId],
    queryFn: () => fetchLotteryStock(tenantId as string, eventId as string),
    enabled: !!tenantId && !!eventId,
    staleTime: 60 * 1000,
  });
}

export interface LotteryTopUser {
  lineUserId: string;
  date: string;
  count: number;
  /** users/line:<id> の displayName。取れなければ null（画面で末尾 6 桁に落とす）。 */
  displayName: string | null;
}

/**
 * cache key: `['promo','lottery','topUsers', tenantId, eventId]`。
 * 上位 10 件 + 表示名の best-effort 解決を 1 つの queryFn に畳む（表示名の失敗はページの
 * エラーにしない = repository 側で null に落ちる）。
 */
export function useLotteryTopUsers(
  tenantId: string | null | undefined,
  eventId: string | null | undefined,
): UseQueryResult<LotteryTopUser[], Error> {
  return useQuery({
    queryKey: ['promo', 'lottery', 'topUsers', tenantId, eventId],
    queryFn: async (): Promise<LotteryTopUser[]> => {
      const rows = await fetchLotteryTopUsers(tenantId as string, eventId as string, 10);
      const names = await fetchLineDisplayNames(rows.map((r) => r.lineUserId));
      return rows.map((r) => ({
        lineUserId: r.lineUserId,
        date: r.date,
        count: r.count,
        displayName: names[r.lineUserId] ?? null,
      }));
    },
    enabled: !!tenantId && !!eventId,
    staleTime: 60 * 1000,
  });
}

// ---------- M-164 第 2 弾: 当選クーポンページ ----------

/**
 * cache key: `['promo','lottery','couponReps', tenantId, eventId]`。
 * 家族代表（各家族の #01）を id → doc（無ければ null）で返す。
 */
export function useLotteryCouponRepresentatives(
  tenantId: string | null | undefined,
  event: LotteryEvent | null | undefined,
): UseQueryResult<Record<string, Coupon | null>, Error> {
  const firstIds = event
    ? [event.everyone.couponIds[0], ...event.prizes.map((p) => p.couponIds[0])].filter(
        (id): id is string => typeof id === 'string' && id.length > 0,
      )
    : [];
  return useQuery({
    queryKey: ['promo', 'lottery', 'couponReps', tenantId, event?.eventId, firstIds],
    queryFn: () => fetchCouponsByIds(firstIds),
    enabled: !!tenantId && !!event,
    staleTime: 60 * 1000,
  });
}

/** cache key: `['promo','lottery','couponUsages', tenantId]`。抽選で付与した個人票の全件。 */
export function useLotteryCouponUsages(
  tenantId: string | null | undefined,
): UseQueryResult<CouponUsage[], Error> {
  return useQuery({
    queryKey: ['promo', 'lottery', 'couponUsages', tenantId],
    queryFn: () => fetchLotteryCouponUsages(tenantId as string),
    enabled: !!tenantId,
    staleTime: 60 * 1000,
  });
}
